// Catalog.ID Client — Reading and writing the member's wallet list
// ============================================================================
// The side-effecting half of `crypto/bitcash-wallets-doc.ts`: the account's
// personal bitcash wallets as one compare-and-set document in the vault's own
// blob namespace, `cid/bitcash-wallets` (cid-encrypted-storage §7.2).
//
// ── why a module rather than a `readCidBlob`/`writeCidBlob` pair per caller ──
// Every edit is read-modify-write against a store with ONE retained generation
// and a compare-and-set on it, so every caller would otherwise have to get the
// conflict loop right. Here it is written once: on a lost CAS, re-read and
// RE-APPLY the edit rather than overwrite. That is only safe because the edits
// are idempotent per slot — add files a NEW slot computed from the document it
// is landing on, rename and revoke name an existing one — so a rival device's
// wallet survives our retry instead of being clobbered by a stale document.
//
// ── what does NOT belong here ───────────────────────────────────────────────
// No caching. The document is small and the blob call is cheap, and a cache
// would have to be invalidated on lock, identity switch, logout and on every
// `bitcash-wallets-save` a module lands — four ways to serve a member a wallet
// list their account no longer has.
//
// DOM-free: the two blob calls are injected, so the retry loop is exercised
// against a fake that really enforces the CAS.

import {
    readCidBlob, writeCidBlob, type CidBlobDeps,
} from './cid-blobs';
import { BlobConflictError } from '../api/blobs';
import {
    BITCASH_WALLETS_BLOB, appendWallet, decodeWalletsDocument, emptyWalletsDocument,
    planWalletRename, revokeWallet as revokeInDocument, activeWallets,
    type BitcashWalletsDocument, type NewWalletRecord,
} from '../crypto/bitcash-wallets-doc';

/**
 * How many times a lost compare-and-set is re-applied before the caller is
 * told. Three, not "until it lands": a document that keeps moving under us
 * means something is writing in a loop, and spinning would turn that into a
 * hung Settings pane and a hung `bitcash-wallets-save`.
 */
const MAX_ATTEMPTS = 3;

/**
 * The two blob calls, injected. Production omits this and gets
 * {@link defaultWalletsStoreDeps}; `finalizeCredentials` supplies its own,
 * because at that moment the account's bag has been written but not yet loaded
 * into `state/keys`, so the derive root has to be handed over explicitly.
 */
export interface WalletsStoreDeps {
    read(): Promise<{ value: unknown; generation: number } | null>;
    write(doc: BitcashWalletsDocument, ifGeneration: number): Promise<{ generation: number }>;
}

/** The store's own deps, over the vault's `cid` namespace. */
export function walletsStoreDeps(blobs?: CidBlobDeps): WalletsStoreDeps {
    return {
        read: () => readCidBlob<unknown>(BITCASH_WALLETS_BLOB, blobs),
        write: (doc, ifGeneration) => writeCidBlob(BITCASH_WALLETS_BLOB, doc, ifGeneration, blobs),
    };
}

export const defaultWalletsStoreDeps: WalletsStoreDeps = walletsStoreDeps();

export interface WalletsSnapshot {
    doc: BitcashWalletsDocument;
    generation: number;
}

/**
 * The account's wallet document, or **null** when it has none yet.
 *
 * Null is a normal answer — a member registered before this landed, or an
 * account whose registration wallet write failed — so callers treat it as an
 * empty list, never as an error. `cid-locked`, `derive-root-missing` and a
 * decryption failure all THROW: each of those means "we cannot see the list",
 * which is a different thing from "there is no list", and conflating them is
 * how a pane ends up offering to create a second registration wallet.
 */
export async function loadWallets(
    deps: WalletsStoreDeps = defaultWalletsStoreDeps,
): Promise<WalletsSnapshot | null> {
    const row = await deps.read();
    if (row === null) return null;
    return { doc: decodeWalletsDocument(row.value), generation: row.generation };
}

/**
 * Read, apply `mutate`, write — retrying the whole cycle on a lost CAS so the
 * edit lands on whatever the account now holds. `mutate` must be PURE and
 * cheap: it runs again per attempt.
 */
export async function mutateWallets(
    mutate: (doc: BitcashWalletsDocument) => BitcashWalletsDocument,
    deps: WalletsStoreDeps = defaultWalletsStoreDeps,
): Promise<WalletsSnapshot> {
    let lastConflict: BlobConflictError | null = null;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const current = await loadWallets(deps);
        const base = current?.doc ?? emptyWalletsDocument();
        const ifGeneration = current?.generation ?? 0;

        const next = mutate(base);
        try {
            const { generation } = await deps.write(next, ifGeneration);
            return { doc: next, generation };
        } catch (err) {
            if (!(err instanceof BlobConflictError)) throw err;
            lastConflict = err;
        }
    }
    throw new Error(
        'Your wallet list is being changed on another device — nothing was saved. '
        + `Try again. (${lastConflict?.message ?? 'conflict'})`);
}

/** Append a wallet at the next free slot and report which slot it took. */
export async function addWallet(
    record: NewWalletRecord,
    deps: WalletsStoreDeps = defaultWalletsStoreDeps,
): Promise<WalletsSnapshot & { slot: number }> {
    // The slot is decided INSIDE the mutator so a retry recomputes it against
    // the document that actually won; `appendWallet` is pure, so capturing the
    // number here is safe.
    let slot = 0;
    const result = await mutateWallets((doc) => {
        const appended = appendWallet(doc, record);
        slot = appended.slot;
        return appended.doc;
    }, deps);
    return { ...result, slot };
}

export async function renameWallet(
    slot: number, label: string,
    deps: WalletsStoreDeps = defaultWalletsStoreDeps,
): Promise<WalletsSnapshot> {
    return await mutateWallets(doc => planWalletRename(doc, slot, label), deps);
}

export async function revokeWallet(
    slot: number,
    deps: WalletsStoreDeps = defaultWalletsStoreDeps,
): Promise<WalletsSnapshot> {
    return await mutateWallets(doc => revokeInDocument(doc, slot), deps);
}

/**
 * The key the Membership Kit's bitcash page prints: the lowest ACTIVE slot,
 * which is the registration wallet on every account that has one.
 *
 * Never throws. §13.1's rule is "print what the account really has", and the
 * caller's fallback — the key the finalize step stashed — is the better answer
 * when the document cannot be read at all; a throw here would lose the sheet
 * instead.
 */
export async function registrationWalletKey(
    deps: WalletsStoreDeps = defaultWalletsStoreDeps,
): Promise<string | null> {
    try {
        const loaded = await loadWallets(deps);
        if (!loaded) return null;
        return activeWallets(loaded.doc)[0]?.key ?? null;
    } catch {
        return null;
    }
}
